import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { confirmDialog } from 'primereact/confirmdialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import LoadingComponent from '../loadingComponent';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import withRouter from '../lib/withRouter';
import Service from '../../services';
import  { baseUrlAdmin } from '../../store/apiConstants';
import './OverrideDecisions.scss';

class OverrideDecisions extends Component {
    constructor(props) {
        super(props);
        this.service = new Service();
        this.toast = React.createRef();

        this.state = {
            loading: false,
            overrides: [],
            filters: {
                isActive: true
            }
        };

        this.statusOptions = [
            { label: 'Active Only', value: true },
            { label: 'Inactive Only', value: false },
            { label: 'All', value: null }
        ];
    }

    componentDidMount() {
        this.fetchOverrides();
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({
            severity: severity,
            summary: summary,
            detail: detail,
            life: 3000
        });
    }

    fetchOverrides = async () => {
        const academicYear = localStorage.getItem('userAcademicYear');
        const { filters } = this.state;

        if (!academicYear) {
            this.showToast('error', 'Error', 'Missing academic year');
            return;
        }

        this.setState({ loading: true });

        try {
            let url = `${baseUrlAdmin}/fee-decision/governance/overrides?academicYear=${academicYear}`;
            
            if (filters.isActive !== null) {
                url += `&isActive=${filters.isActive}`;
            }

            const response = await this.service.get(url, true);

            if (response?.status && response?.res?.success) {
                this.setState({
                    overrides: response.res.data || []
                });
            } else {
                this.showToast('error', 'Error', 'Failed to fetch overrides');
            }
        } catch (error) {
            console.error('Error fetching overrides:', error);
            this.showToast('error', 'Error', 'Failed to fetch overrides');
        } finally {
            this.setState({ loading: false });
        }
    }

    handleRemoveOverride = (rowData) => {
        confirmDialog({
            message: `Are you sure you want to remove the override for ${rowData.studentName || rowData.studentId}?`,
            header: 'Confirm Removal',
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.removeOverride(rowData),
            reject: () => {}
        });
    }

    removeOverride = async (rowData) => {
        const academicYear = localStorage.getItem('userAcademicYear');

        this.setState({ loading: true });

        try {
            const url = `${baseUrlAdmin}/fee-decision/governance/overrides/${rowData.studentId}?academicYear=${academicYear}`;
            
            const response = await this.service.delete(url, true);

            if (response?.status && response?.res?.success) {
                this.showToast('success', 'Success', 'Override removed successfully');
                this.fetchOverrides(); // Refresh list
            } else {
                this.showToast('error', 'Error', response?.res?.message || 'Failed to remove override');
            }
        } catch (error) {
            console.error('Error removing override:', error);
            this.showToast('error', 'Error', 'Failed to remove override');
        } finally {
            this.setState({ loading: false });
        }
    }

    // Body Templates
    studentNameBodyTemplate = (rowData) => {
        return (
            <div>
                <div className="student-name">{rowData.studentName || 'N/A'}</div>
                <div className="student-id">{rowData.admissionNo || rowData.studentId}</div>
            </div>
        );
    }

    overrideBandBodyTemplate = (rowData) => {
        const band = rowData.overrideBand;
        
        let severity = null;
        let style = null;
        
        switch (band) {
            case 'CRITICAL': style = { backgroundColor: '#FFA6BC', color: '#BF0031' }; break;
            case 'HIGH': style = { backgroundColor: '#FFEDD5', color: '#C2410C' }; break; // Explicit Dark Orange
            case 'MEDIUM': style = { backgroundColor: '#FEF9C3', color: '#854D0E' }; break; // Explicit Yellow
            case 'LOW': style = { backgroundColor: '#DCFCE7', color: '#268900' }; break;
            case 'NONE': style = { backgroundColor: '#E6E8EA', color: '#4B5563' }; break;
            default: style = { backgroundColor: '#E6E8EA', color: '#4B5563' };
        }

        return <Tag value={band} severity={severity} style={style} />;
    }

    dateBodyTemplate = (rowData) => {
        if (!rowData.updatedAt) return 'N/A';
        
        const date = new Date(rowData.updatedAt);
        // Keep timestamp for Audit like fields if needed, or strip if user insisted.
        // Assuming user implies Date fields. 
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    overrideUntilBodyTemplate = (rowData) => {
        if (!rowData.overrideUntil) return 'Permanent';
        
        const date = new Date(rowData.overrideUntil);
        const isExpired = date < new Date();
        
        return (
            <div>
                <div>{date.toLocaleDateString('en-GB')}</div>
                {isExpired && <Tag value="Expired" severity="danger" />}
            </div>
        );
    }

    statusBodyTemplate = (rowData) => {
        return (
            <Tag 
                value={rowData.isActive ? 'Active' : 'Inactive'} 
                severity={rowData.isActive ? 'success' : 'secondary'}
            />
        );
    }

    actionBodyTemplate = (rowData) => {
        return (
            <Authorizer permId={PERMISSIONS.FDE_DELETE_OVERRIDE}>
                <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-danger p-button-text"
                    onClick={() => this.handleRemoveOverride(rowData)}
                    disabled={!rowData.isActive}
                    tooltip="Remove Override"
                    tooltipOptions={{ position: 'left' }}
                />
            </Authorizer>
        );
    }

    render() {
        const { loading, overrides, filters } = this.state;

        if (loading && overrides.length === 0) {
            return <LoadingComponent />;
        }

        return (
            <div className="override-decisions">
                <Toast ref={this.toast} position="bottom-right" />
                <ConfirmDialog />
                
                <Card className="header-card">
                    <div className="header-content">
                        <div className="header-left">
                            <h2>🗂️ Override Decisions</h2>
                            <p className="description-text">
                                View and manage all risk override decisions. These are manual adjustments made by authorized users to override system-calculated risk bands.
                            </p>
                        </div>
                        <div className="header-stats">
                            <div className="stat-item">
                                <span className="stat-label">Total Overrides</span>
                                <span className="stat-value">{overrides.length}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Active</span>
                                <span className="stat-value active">{overrides.filter(o => o.isActive).length}</span>
                            </div>
                        </div>
                    </div>
                </Card>

                <Card className="filters-card">
                    <div className="filters-section">
                        <div className="filter-item">
                            <label>Status Filter</label>
                            <Dropdown
                                value={filters.isActive}
                                options={this.statusOptions}
                                onChange={(e) => {
                                    this.setState({ 
                                        filters: { ...filters, isActive: e.value } 
                                    }, () => this.fetchOverrides());
                                }}
                                placeholder="Select Status"
                            />
                        </div>
                        <div className="filter-item">
                            <Button 
                                label="Refresh" 
                                icon="pi pi-refresh" 
                                onClick={this.fetchOverrides}
                                loading={loading}
                            />
                        </div>
                    </div>
                </Card>

                <Card className="table-card">
                    <DataTable
                        value={overrides}
                        paginator
                        rows={20}
                        loading={loading}
                        emptyMessage="No override decisions found"
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        className="overrides-table"
                        stripedRows
                        scrollable
                    >
                        <Column 
                            header="Student" 
                            body={this.studentNameBodyTemplate}
                            sortable
                            sortField="studentName"
                            style={{ minWidth: '200px' }}
                        />
                        <Column 
                            field="className" 
                            header="Class"
                            style={{ minWidth: '120px' }}
                        />
                        <Column 
                            header="Override Band" 
                            body={this.overrideBandBodyTemplate}
                            sortable
                            sortField="overrideBand"
                            style={{ minWidth: '150px' }}
                        />
                        <Column 
                            field="justification" 
                            header="Justification"
                            style={{ minWidth: '250px' }}
                        />
                        <Column 
                            header="Valid Until" 
                            body={this.overrideUntilBodyTemplate}
                            sortable
                            sortField="overrideUntil"
                            style={{ minWidth: '150px' }}
                        />
                        <Column 
                            header="Status" 
                            body={this.statusBodyTemplate}
                            sortable
                            sortField="isActive"
                            style={{ minWidth: '120px' }}
                        />
                        <Column 
                            field="updatedBy" 
                            header="Updated By"
                            style={{ minWidth: '150px' }}
                        />
                        <Column 
                            header="Updated At" 
                            body={this.dateBodyTemplate}
                            sortable
                            sortField="updatedAt"
                            style={{ minWidth: '180px' }}
                        />
                        <Column 
                            header="Actions" 
                            body={this.actionBodyTemplate}
                            style={{ minWidth: '100px' }}
                        />
                    </DataTable>
                </Card>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    user: state.currentUserPerms?.userInfo
});

export default connect(mapStateToProps)(withRouter(OverrideDecisions));
